import * as Phaser from 'phaser';
import {
  CANONICAL_ROOMS,
  ISO_TILE_H,
  ISO_TILE_W,
  getCanonicalRoom,
  isoDepth,
  isoWorldBounds,
  screenToTile,
  toScreen,
  type CanonicalRoom,
} from '@game/shared';
import { EDITOR_SCENE_KEY } from '../scenes/sceneKeys';

const TILE_PALETTE = [1, 2, 5, 9, 17, 33] as const;
const MAP_FALLBACK = { width: 40, height: 30 };
const TILE_COLORS = new Map<number, number>([
  [1, 0x6fa45f],
  [2, 0x8fbe77],
  [5, 0xb88958],
  [9, 0x7d8790],
  [17, 0x8a6a4b],
  [33, 0x5677a6],
]);

type PaintLayerName = 'floor' | 'walls' | 'decorations';

interface EditorLayer {
  name: PaintLayerName;
  tiles: number[];
  graphics: Phaser.GameObjects.Graphics;
}

function readEditRoomId(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('edit')?.trim() || 'expedition:verdana_shallow';
}

function mapSizeForRoom(room: CanonicalRoom | null): { width: number; height: number } {
  if (room?.bounds) {
    return {
      width: Math.max(1, Math.ceil(room.bounds.width / 32)),
      height: Math.max(1, Math.ceil(room.bounds.height / 32)),
    };
  }
  if (room?.kind === 'home') return { width: 20, height: 15 };
  if (room?.kind === 'town') return { width: 60, height: 42 };
  return MAP_FALLBACK;
}

export class EditorScene extends Phaser.Scene {
  public static readonly KEY = EDITOR_SCENE_KEY;

  private roomId = '';
  private mapWidth = MAP_FALLBACK.width;
  private mapHeight = MAP_FALLBACK.height;
  private selectedTile: number = TILE_PALETTE[0];
  private selectedLayerName: PaintLayerName = 'floor';
  private layers = new Map<PaintLayerName, EditorLayer>();
  private hoverDiamond!: Phaser.GameObjects.Graphics;
  private hud!: Phaser.GameObjects.Text;
  private toolbar!: Phaser.GameObjects.Text;
  private isDraggingCamera = false;
  private dragStart = { x: 0, y: 0, scrollX: 0, scrollY: 0 };

  constructor() {
    super({ key: EDITOR_SCENE_KEY });
  }

  create(): void {
    this.roomId = readEditRoomId();
    const room = getCanonicalRoom(this.roomId);
    const size = mapSizeForRoom(room);
    this.mapWidth = size.width;
    this.mapHeight = size.height;

    this.cameras.main.setBackgroundColor('#111827');
    const bounds = isoWorldBounds(this.mapWidth, this.mapHeight);
    this.cameras.main.setBounds(0, -ISO_TILE_H, bounds.width, bounds.height + ISO_TILE_H * 2);
    this.cameras.main.centerOn(bounds.width / 2, bounds.height / 2);
    this.cameras.main.setZoom(1);

    this.createLayers();
    this.drawAllLayers();
    this.createUi(room);
    this.installInput();
    this.updateToolbar();
  }

  private createLayers(): void {
    for (const name of ['floor', 'walls', 'decorations'] as const) {
      const graphics = this.add.graphics();
      graphics.setDepth(name === 'floor' ? -20 : name === 'walls' ? -10 : 0);
      const fill = name === 'floor' ? TILE_PALETTE[0] : 0;
      this.layers.set(name, {
        name,
        tiles: new Array(this.mapWidth * this.mapHeight).fill(fill),
        graphics,
      });
    }
    this.hoverDiamond = this.add.graphics().setDepth(1000);
  }

  private createUi(room: CanonicalRoom | null): void {
    this.hud = this.add.text(10, 10, '', {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#f8fafc',
      backgroundColor: 'rgba(15,23,42,0.86)',
      padding: { left: 8, right: 8, top: 6, bottom: 6 },
    }).setScrollFactor(0).setDepth(2000);

    const knownRooms = Object.keys(CANONICAL_ROOMS).join(', ');
    this.toolbar = this.add.text(10, this.scale.height - 10, '', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#e2e8f0',
      backgroundColor: 'rgba(15,23,42,0.86)',
      padding: { left: 8, right: 8, top: 6, bottom: 6 },
      wordWrap: { width: this.scale.width - 20 },
    }).setOrigin(0, 1).setScrollFactor(0).setDepth(2000);

    this.hud.setText([
      `room: ${this.roomId}${room ? '' : ' (unknown, fallback map)'}`,
      `known: ${knownRooms}`,
      'hover: outside map',
    ]);
  }

  private installInput(): void {
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isDraggingCamera) {
        const camera = this.cameras.main;
        camera.scrollX = this.dragStart.scrollX - (pointer.x - this.dragStart.x) / camera.zoom;
        camera.scrollY = this.dragStart.scrollY - (pointer.y - this.dragStart.y) / camera.zoom;
        return;
      }
      this.updateHover(pointer);
    });

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown() || pointer.middleButtonDown()) {
        this.isDraggingCamera = true;
        this.dragStart = {
          x: pointer.x,
          y: pointer.y,
          scrollX: this.cameras.main.scrollX,
          scrollY: this.cameras.main.scrollY,
        };
        return;
      }
      this.paintAtPointer(pointer, pointer.leftButtonDown() ? this.selectedTile : 0);
    });

    this.input.on('pointerup', () => {
      this.isDraggingCamera = false;
    });

    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _objects: unknown[], _dx: number, dy: number) => {
      const nextZoom = Phaser.Math.Clamp(this.cameras.main.zoom + (dy > 0 ? -0.1 : 0.1), 0.45, 2.5);
      this.cameras.main.setZoom(nextZoom);
      this.updateToolbar();
    });

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key >= '1' && event.key <= String(TILE_PALETTE.length)) {
        this.selectedTile = TILE_PALETTE[Number(event.key) - 1];
      } else if (event.key.toLowerCase() === 'f') {
        this.selectedLayerName = 'floor';
      } else if (event.key.toLowerCase() === 'w') {
        this.selectedLayerName = 'walls';
      } else if (event.key.toLowerCase() === 'd') {
        this.selectedLayerName = 'decorations';
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        this.paintAtPointer(this.input.activePointer, 0);
      }
      this.updateToolbar();
    });
  }

  private paintAtPointer(pointer: Phaser.Input.Pointer, gid: number): void {
    const tile = this.pointerToTile(pointer);
    if (!tile) return;
    const layer = this.layers.get(this.selectedLayerName);
    if (!layer) return;
    layer.tiles[tile.ty * this.mapWidth + tile.tx] = gid;
    this.drawLayer(layer);
    this.updateHover(pointer);
  }

  private pointerToTile(pointer: Phaser.Input.Pointer): { tx: number; ty: number } | null {
    const camera = this.cameras.main;
    const worldX = camera.scrollX + pointer.x / camera.zoom;
    const worldY = camera.scrollY + pointer.y / camera.zoom;
    const tile = screenToTile(worldX, worldY, this.mapHeight);
    const tx = Math.floor(tile.tx);
    const ty = Math.floor(tile.ty);
    if (tx < 0 || ty < 0 || tx >= this.mapWidth || ty >= this.mapHeight) return null;
    return { tx, ty };
  }

  private updateHover(pointer: Phaser.Input.Pointer): void {
    const tile = this.pointerToTile(pointer);
    this.hoverDiamond.clear();
    if (!tile) {
      this.hud.setText([
        `room: ${this.roomId}`,
        `map: ${this.mapWidth}x${this.mapHeight} zoom=${this.cameras.main.zoom.toFixed(2)}`,
        'hover: outside map',
      ]);
      return;
    }

    this.drawDiamond(this.hoverDiamond, tile.tx, tile.ty, 0x38bdf8, 0.18, 0xffffff, 0.9);
    const screen = toScreen(tile.tx, tile.ty, this.mapHeight);
    this.hud.setText([
      `room: ${this.roomId}`,
      `map: ${this.mapWidth}x${this.mapHeight} zoom=${this.cameras.main.zoom.toFixed(2)}`,
      `hover: tx=${tile.tx} ty=${tile.ty} screen=${Math.round(screen.x)},${Math.round(screen.y)} depth=${isoDepth(tile.tx, tile.ty)}`,
    ]);
  }

  private drawAllLayers(): void {
    for (const layer of this.layers.values()) {
      this.drawLayer(layer);
    }
  }

  private drawLayer(layer: EditorLayer): void {
    layer.graphics.clear();
    for (let ty = 0; ty < this.mapHeight; ty++) {
      for (let tx = 0; tx < this.mapWidth; tx++) {
        const gid = layer.tiles[ty * this.mapWidth + tx];
        if (gid <= 0) continue;
        const color = TILE_COLORS.get(gid) ?? 0xa3a3a3;
        const alpha = layer.name === 'floor' ? 0.92 : 0.72;
        const lineAlpha = layer.name === 'floor' ? 0.22 : 0.45;
        this.drawDiamond(layer.graphics, tx, ty, color, alpha, 0x0f172a, lineAlpha);
      }
    }
  }

  private drawDiamond(
    graphics: Phaser.GameObjects.Graphics,
    tx: number,
    ty: number,
    fill: number,
    fillAlpha: number,
    stroke: number,
    strokeAlpha: number,
  ): void {
    const p = toScreen(tx, ty, this.mapHeight);
    const halfW = ISO_TILE_W / 2;
    const halfH = ISO_TILE_H / 2;
    graphics.fillStyle(fill, fillAlpha);
    graphics.lineStyle(1, stroke, strokeAlpha);
    graphics.beginPath();
    graphics.moveTo(p.x, p.y);
    graphics.lineTo(p.x + halfW, p.y + halfH);
    graphics.lineTo(p.x, p.y + ISO_TILE_H);
    graphics.lineTo(p.x - halfW, p.y + halfH);
    graphics.closePath();
    graphics.fillPath();
    graphics.strokePath();
  }

  private updateToolbar(): void {
    this.toolbar.setText(
      `LMB paint  RMB/MMB drag  Wheel zoom  Del erase  Layer F/W/D: ${this.selectedLayerName}  Tile 1-${TILE_PALETTE.length}: gid ${this.selectedTile}`,
    );
  }
}
